/**
 * Shared Prompt Content for Builder Agents
 * 
 * This file contains reusable prompt sections for ExecutionAgent and MultiSlideBuilderAgent.
 * Each section is exported separately so you can import only what you need.
 * 
 * Usage:
 *   import { DENSITY_GUIDELINES, CHART_SOPHISTICATION } from './prompts';
 *   const prompt = `${DENSITY_GUIDELINES}\n${CHART_SOPHISTICATION}`;
 * 
 * Or import everything:
 *   import * as PromptContent from './prompts';
 */

// =============================================================================
// CORE QUALITY STANDARDS
// =============================================================================

/**
 * Investment banking slide quality overview - the "why" behind density
 */
export const IB_QUALITY_OVERVIEW = `
## Understanding Investment Banking Slide Quality

Investment banking slides are fundamentally information systems, not visual presentations. Every element exists to convey specific data, relationships, or insights that decision-makers need. The slides you'll be creating serve analysts, associates, VPs, and MDs who are intimately familiar with the content and need maximum information density to support rapid decision-making in meetings.
`.trim();

/**
 * Detailed density guidelines - what density means and how to achieve it
 */
export const DENSITY_GUIDELINES = `
### The Nature of Density

Density in banking slides isn't about cramming content arbitrarily - it's about ELIMINATING WHITESPACE while maintaining readability and hierarchy. A well-constructed banking slide might have 200-300 words of text across headers, labels, annotations, and footnotes, plus multiple data visualizations, all fitting within the content area without feeling cluttered. This works because:

1. **Hierarchical typography** creates clear reading paths - large headers, medium subheaders, small body text, and tiny footnotes all serve distinct purposes
2. **MINIMAL white space** exists only where it aids comprehension - between distinct sections, around key callouts, or as visual separators. EVERY PIXEL OF WHITESPACE MUST JUSTIFY ITS EXISTENCE.
3. **Layered information** allows dense packing - you might have a base chart, overlay annotations, embedded data tables, and surrounding context all occupying the same visual region

When you examine reference slides and components, look for how they achieve density. A table might use very compact line spacing (1.0 or less) but compensate with subtle separator rows. A chart might have 15+ bars but use tiny, precise labels and company logo icons to maintain clarity. The goal is always: how much relevant information can fit while remaining scannable?
`.trim();

/**
 * Whitespace philosophy - the balance between density and readability
 */
export const WHITESPACE_PHILOSOPHY = `
### The Role of Whitespace

Whitespace in banking slides is functional, not decorative. It creates visual hierarchy and groups related information. The key insight: whitespace should exist BETWEEN logical groups, not WITHIN them.

**CRITICAL: MINIMIZE WHITESPACE AGGRESSIVELY.** Banking slides are not art galleries - they are information delivery systems. Empty space is wasted opportunity. If you see a gap larger than necessary for visual separation, fill it with relevant content: additional data labels, footnotes, annotations, or supporting metrics. The only acceptable whitespace is functional whitespace that improves readability or creates necessary visual hierarchy. NEVER leave large empty areas just because you've run out of obvious content to add.

**BUT: Minimizing Whitespace ≠ Random Placement**

Aggressive whitespace minimization is NOT a license to place shapes randomly or create ugly overlaps. The distinction is critical:

**CORRECT density**: Elements are tightly packed but deliberately positioned. Charts align with their legends. Tables sit cleanly adjacent to visualizations. Annotations are precisely placed relative to what they annotate. Text boxes have clear boundaries. Everything has a logical spatial relationship.

**INCORRECT density**: Shapes overlap in confusing ways. Text boxes cover parts of charts unintentionally. Elements are scattered without alignment. The composition feels chaotic rather than information-rich.

When minimizing whitespace, maintain:
- **Clean edges**: Shapes should align to invisible grids, not float randomly
- **Intentional layering**: If elements overlap, it's deliberate (like an annotation callout over a chart), not accidental
- **Visual grouping**: Related elements are spatially clustered, unrelated elements have clear separation
- **Reading flow**: The eye can follow a logical path through the content

Think of it as Tetris, not a pile of blocks. Every piece fits precisely, maximizing coverage while maintaining structure.
`.trim();

// =============================================================================
// CHART SOPHISTICATION
// =============================================================================

/**
 * Chart sophistication guidelines with examples
 */
export const CHART_SOPHISTICATION = `
Banking charts are rarely simple. Consider different levels of sophistication:

**Basic level** (AVOID): A revenue chart might show 4 quarterly bars with axis labels and a title. This conveys minimal information and wastes the available space.

**Intermediate level** (AVOID): The same chart adds exact value labels on each bar, a legend, and a footnote about data sources. Better, but still simple.

**Advanced level** (target this): The chart becomes a composition - perhaps 4 quarterly mini-charts in a 2x2 grid, each showing multiple segments as stacked or grouped bars, with YoY change percentages annotated above, median lines overlaid, company logo icons identifying segments, a shared legend, and 2-3 footnotes explaining methodology and assumptions. Or alternatively, a single large chart with 12-16 bars (3-4 segments across 4 quarters) where each bar has its exact value labeled, segments are color-coded with a legend, key outlier bars have callout annotations explaining the drivers, and there's a small embedded table in one corner showing summary statistics.

The sophistication comes from composition and layering. When searching for chart components, you might find that the presentation style uses:
- Dual-axis charts with two different metrics overlaid
- Charts embedded within table cells
- Multiple related charts arranged in grids with shared legends
- Heavily annotated time series with shaded regions marking important periods
- Comparison charts where each bar is tagged with a company logo or icon at its base

None of these patterns is mandatory, but recognizing them when you see them in components helps you understand what "sophisticated" means for this particular presentation style.

#### Detailed Chart Sophistication Examples

To illustrate what "advanced level" truly means, here are concrete examples of sophisticated banking chart patterns:

**Example 1: Time Series with Layered Annotations (like the Yoda Revenue Multiple chart)**

A line chart showing a metric over time (e.g., revenue multiple, stock price, valuation ratio) that includes:
- **Base line chart**: Dense time series with 50-100+ data points showing continuous trend
- **Shaded regions**: Translucent colored boxes highlighting important periods (e.g., light green box labeled "Overvalued Growth Expectations" spanning a specific date range, light blue box for "Concerns Over Growth and Margins")
- **Reference lines**: Horizontal lines marking key thresholds or benchmarks (e.g., "Weighted Offer: 4.6x" as a dashed line across the chart)
- **Embedded data table**: Small table in the upper right showing summary statistics (e.g., "NTM AV / Revenue Multiple Statistics" with rows for "Last 30 Days", "Since Lockup #1", "Since Lockup #2", "All-time" and columns for "Average" and "Median")
- **Multiple callout annotations**: Text boxes with arrows pointing to specific inflection points on the line (e.g., "Concerns Over growth and margins, Rotation out of growth names" pointing to a decline, "Thoughts on Profit Sustainability" pointing to another region)
- **Axis labels and gridlines**: Y-axis showing percentage or multiple values (2x, 4x, 6x, etc.), X-axis showing dense date labels (monthly or quarterly)
- **Footnotes**: 2-3 lines of small text at the bottom explaining data sources, methodology, or assumptions
- **Header context**: Subtitle under main title providing additional context (e.g., "AV / NTM Revenue since 4/20/2018 IPO")

This creates a chart where the visual density is extremely high - the line itself, multiple annotation layers, embedded tables, shaded regions, and reference lines all occupy the same space, but each serves a distinct informational purpose.

**Example 2: Multi-Panel Comparison Grid**

A 2x2 or 2x3 grid of related charts showing comparative analysis:
- **Consistent structure**: Each panel is a complete chart (bar, line, or scatter) with its own axes and labels
- **Shared legend**: One legend positioned centrally or at the top serves all panels, using consistent color coding
- **Panel titles**: Each chart has a clear header indicating what it shows (e.g., "Q1 2023", "Q2 2023", "Q3 2023", "Q4 2023")
- **Synchronized scales**: Y-axes use the same range across panels for easy comparison
- **Annotations per panel**: Each chart might have 1-2 specific callouts highlighting outliers or key insights
- **Summary metrics**: Small text labels showing aggregate statistics for each panel (e.g., "Total: $450M", "Growth: +12%")
- **Separator styling**: Subtle borders or background shading distinguishing each panel
- **Compact spacing**: Minimal gaps between panels (10-15 points) to maximize chart size while maintaining clarity

**Example 3: Stacked/Grouped Bar Chart with Dense Labeling**

A bar chart showing categorical comparisons with maximum information density:
- **12-20 bars**: Multiple categories (e.g., 4 quarters) × multiple segments (e.g., 3-5 product lines or business units)
- **Stacked or grouped arrangement**: Bars show composition (stacked) or side-by-side comparison (grouped)
- **Value labels on every segment**: Each bar segment displays its exact value, positioned inside or just outside the bar
- **Percentage annotations**: Additional labels showing percentage of total or YoY change (e.g., "+15%" above a bar)
- **Company logos or icons**: Small icons at the base of each bar or segment identifying the entity (especially for competitor comparisons)
- **Color-coded legend**: 5-8 colors with clear legend, possibly using brand colors for specific companies
- **Reference lines**: Horizontal lines marking targets, averages, or prior period values
- **Axis customization**: Y-axis might show dual scales (left: absolute values, right: percentages) or use abbreviated notation ($M, $B)
- **Embedded table**: Small data table in an empty corner showing exact values, growth rates, or market share percentages
- **Callout boxes**: 2-3 text boxes with arrows pointing to specific bars, explaining outliers or key drivers (e.g., "Spike driven by M&A activity", "Seasonal decline typical for Q4")

**Example 4: Dual-Axis Overlay Chart**

A chart combining two different metrics on the same visualization:
- **Primary metric**: Bar chart showing one metric (e.g., revenue in $M) on the left Y-axis
- **Secondary metric**: Line chart overlaid showing a different metric (e.g., margin % or growth rate) on the right Y-axis
- **Distinct styling**: Bars use solid fills, line uses contrasting color with markers at each data point
- **Dual legends**: Clear indication of which metric corresponds to which axis and visual element
- **Synchronized X-axis**: Both metrics share the same time periods or categories
- **Annotations for both metrics**: Callouts might reference either the bars or the line (e.g., "Revenue peaked at $500M" pointing to a bar, "Margin compression began Q3" pointing to the line)
- **Shaded regions**: Might highlight periods relevant to both metrics (e.g., "Restructuring period" affecting both revenue and margins)
- **Data labels**: Values shown on bars and/or line markers
- **Footnotes**: Explaining the relationship between the two metrics or methodology differences

**Example 5: Waterfall Chart with Detailed Attribution**

A waterfall chart showing how a starting value transitions to an ending value through multiple components:
- **10-15 bars**: Starting value, 8-12 contributing factors (positive and negative), ending value
- **Color coding**: Positive contributions in one color (e.g., green), negative in another (e.g., red), start/end in neutral (e.g., gray)
- **Connector lines**: Subtle lines connecting the top of each bar to the base of the next, showing the flow
- **Value labels**: Each bar labeled with its exact contribution amount and possibly percentage of total change
- **Cumulative labels**: Running total shown at the top of each bar
- **Category labels**: Detailed X-axis labels explaining each factor (e.g., "Organic Growth", "M&A Impact", "FX Headwinds", "Cost Savings", "One-time Charges")
- **Annotations**: Callout boxes explaining key drivers (e.g., "M&A contributed $50M, primarily from XYZ acquisition")
- **Summary metrics**: Text box showing total change, percentage change, and possibly CAGR
- **Footnotes**: Methodology for calculating each component, data sources, assumptions

**Example 6: Scatter Plot with Segmentation and Annotations**

A scatter plot showing relationships between two variables with rich context:
- **50-100+ data points**: Each point represents an entity (company, product, region, time period)
- **Size encoding**: Point size represents a third variable (e.g., market cap, revenue size)
- **Color encoding**: Point color represents categories (e.g., industry sectors, geographic regions)
- **Labels on key points**: 10-20 points labeled with entity names, especially outliers or points of interest
- **Quadrant lines**: Vertical and horizontal reference lines dividing the chart into quadrants, with labels explaining each quadrant (e.g., "High Growth, High Margin", "Low Growth, High Margin")
- **Trend line**: Regression line or curve showing overall relationship, possibly with R² value labeled
- **Callout clusters**: Groups of related points circled or highlighted with annotations explaining the cluster
- **Legend**: Explaining size and color encodings
- **Axis labels**: Clear indication of what each axis represents, with units
- **Embedded statistics**: Small table showing correlation coefficient, median values, or other summary stats

**Example 7: Heatmap Table with Embedded Visualizations**

A hybrid between a table and a chart, showing dense comparative data:
- **Grid structure**: Rows and columns creating 20-50+ cells
- **Color-coded cells**: Background colors representing values (gradient from red to green, or categorical colors)
- **Values in cells**: Exact numbers or percentages displayed in each cell
- **Embedded mini-charts**: Small bar charts, line charts, or sparklines within certain cells showing trends
- **Row/column headers**: Multi-level headers creating hierarchical categorization
- **Separator styling**: Heavier borders between major sections, lighter borders within sections
- **Annotations**: Callout boxes pointing to specific cells or regions, explaining patterns or outliers
- **Legend**: Color scale explanation, possibly with threshold values
- **Summary row/column**: Totals, averages, or other aggregate metrics in a distinct style

**Key Principles Across All Examples:**

1. **Layering**: Multiple information types occupy the same visual space (base chart + annotations + reference lines + embedded tables)
2. **Precision**: Every data point is labeled or easily readable; no ambiguity about values
3. **Context**: Annotations, callouts, and footnotes explain what the data means, not just what it is
4. **Density**: Minimal whitespace; every region of the chart serves an informational purpose
5. **Hierarchy**: Despite density, clear visual hierarchy guides the eye (large title → main chart → annotations → footnotes)
6. **Consistency**: Styling (colors, fonts, line weights) follows presentation-wide patterns
7. **Composition**: Charts are often combined with other elements (tables, text boxes, icons) to create comprehensive information systems

When you search for chart components and see patterns like these, recognize that this is the sophistication level to target. Don't simplify to "just a chart" - embrace the complexity and information richness that makes banking slides effective decision-support tools.
`.trim();

// =============================================================================
// TABLE SOPHISTICATION
// =============================================================================

/**
 * Table sophistication guidelines with categories
 */
export const TABLE_SOPHISTICATION = `
### Detailed Table Sophistication Examples

**IMPORTANT NOTE**: The examples below are provided to illustrate the *level of sophistication and information density* typical in banking tables. They are NOT explicit instructions on what to build. ALWAYS use the actual reference slides you find through your search as the primary structural guide. These examples serve only to help you recognize sophisticated tables when you see them and understand what "advanced level" means in the banking context. Adapt your approach based on what you discover in the reference materials.

Banking presentations feature three distinct categories of tables, each with unique complexity patterns. Understanding these archetypes is essential for creating information-dense, professional tables.

#### Category 1: Financial Statement Tables

These tables present financial data following standard accounting structures (income statements, cash flow statements, balance sheets, or financial projections). The sophistication comes from dense time series, hierarchical line items, and rich annotations.

**Example 1A: Multi-Year Financial Projection Table**

A comprehensive financial model table spanning 3-5 years with full P&L/cash flow detail:

**Structural features**:
- **Row hierarchy**: 30-50 rows organized into clear sections (Revenue, Operating Expenses, EBITDA, Adjustments, etc.)
  - Primary section headers in bold with background shading (e.g., "Gross Gaming Revenue", "Operating Expenses", "MOC EBITDA")
  - Indented sub-items showing components (e.g., under Revenue: "Slot Machines", "Tables", "Poker", "Retail Sportsbook")
  - Calculation rows showing subtotals and totals (with borders above/below for emphasis)
  - Adjustment rows with parentheses prefix: "(-) Comps", "(-) Operating Expenses", "(+) Ground lease Adjustment"
- **Column structure**: 6-8 columns total
  - First column: Line item descriptions (left-aligned, varying indentation levels)
  - Remaining columns: Fiscal years (e.g., "2019E", "2020E", "2021E", "2022E", "2023E")
  - Column headers in bold with background shading
- **Merged cells**: Section header rows span all columns to create visual breaks

**Content density**:
- **Mixed value types**:
  - Absolute values: Dollar amounts in millions ($155, $184, $193)
  - Percentages: Growth rates and margins (23.9%, 12.7%, 13.4%)
  - Text annotations: "NM" for "not meaningful", descriptive labels
- **Inline calculations**: Clear indication of additive vs. subtractive items through formatting
- **Multiple metric types**: Dollars, percentages, absolute counts, ratios all within same table
- **Footnote markers**: Superscript numbers linking to explanatory notes below table

**Visual sophistication**:
- **Color-coded sections**: Key metrics (Total Revenue, Net Revenue, MOC EBITDA, ERI EBITDA) with light background shading
- **Number formatting**: 
  - Dollars shown without decimals when in millions ($259, $320)
  - Percentages to one decimal (23.9%, 5.3%)
  - Parentheses for negative values or subtractive items ($78), ($93)
- **Alignment precision**:
  - Labels left-aligned with visible indentation hierarchy
  - Numbers right-aligned within columns
  - Percentage signs consistently positioned
- **Border hierarchy**:
  - Heavy borders separating major sections
  - Medium borders above/below subtotal rows
  - Light borders for regular line items
  - Double borders above final total rows
- **Growth metrics**: Percentage growth shown below each value or in dedicated "% Growth" rows
- **KPI callouts**: Key metrics table at bottom showing Win/Unit/Day, Win/Table/Day, RevPAR

**Example 1B: Monthly Cash Flow Build Table**

A detailed cash flow waterfall showing month-by-month cash position:

**Structural features**:
- **Time series columns**: 8-12 columns representing consecutive months (July, August, September, October, November, December, January, February, March)
- **Cash flow line items**:
  - Starting cash position
  - Operating cash flows (EBITDA, cash adjustments)
  - Financing activities (loans, preferred draws, funding fees)
  - Ending cash position with and without certain adjustments
- **Separator rows**: Blank rows or light borders between EBITDA section, cash adjustments section, and ending position
- **Calculation flow**: Each column shows the waterfall from beginning to ending cash

**Content density**:
- **Parenthetical values**: Negative values shown as ($874), ($905) for cash outflows
- **Dashed lines or special markers**: "(-) Bangkok Bank Loan" with dash prefix showing uses of cash
- **Positive inflows**: "(+) KH Preferred Draw" showing cash contributions as $15,000, $37,000
- **Running totals**: "Ending Cash Position (Pre-MOC Draw)" followed by adjustments, then final "Ending Cash Position"
- **Small dollar amounts**: Precise values like $11,172, $10,298 showing detailed cash tracking

**Visual sophistication**:
- **Highlighted rows**: Key rows like "Cash", "Ending Cash Position (Pre-MOC Draw)", "Ending Cash Position" with background shading
- **Underlines and overlines**: Single or double lines above ending position rows
- **Color coding**: Possibly red/green for negative/positive cash changes (or red highlights for concerning low cash levels)
- **Compact spacing**: Narrow columns (monthly intervals) creating horizontal density

#### Category 2: Quantitative Comparison Tables

These tables compare numerical metrics across multiple entities, time periods, or scenarios. They blend data density with comparative insights through formatting and annotations.

**Example 2A: Offer Premia Analysis Table**

A table showing implied premiums across multiple merger scenarios and metrics:

**Structural features**:
- **Nested column headers**: 
  - Top-level: Major categories or time periods ("1-Month Ago", "1-Week Ago", "Pre-Announcement", "Current", etc.)
  - Mid-level: Sub-categories within each period ("High", "Low", "Average")
  - Bottom-level: Individual metrics or dates
- **Row structure**: 4-6 rows showing different election types or consideration structures:
  - "Cash Election"
  - "Equity Election"
  - "Blended Offer"
  - "Pro-Rumor Share Price"
- **Multiple comparison dimensions**: Same metrics shown across time, across deal structures, across pricing scenarios

**Content density**:
- **Percentage values**: Premium percentages with consistent decimal formatting (23.6%, 11.1%, (13.3)%)
- **Negative values**: Parentheses indicating dilution or negative premiums ((9.2)%, (14.0)%)
- **Absolute values**: Mixed with percentages in some rows (1.0, (9.2), (29.2), 2.0)
- **Highlighting**: Circled or boxed values showing key metrics (e.g., "14.6" circled in red)
- **Italicized projections**: Forward-looking values in italics (e.g., "Pro-Rumor Share Price" row)

**Visual sophistication**:
- **Color-coded cells**: Green for positive premiums, red for dilution/discounts, yellow for blended scenarios
- **Thick borders**: Separating major row sections (Cash vs. Equity vs. Blended)
- **Column shading**: Alternating light gray for time periods to aid eye tracking across wide table
- **Header emphasis**: Bold, centered headers with background colors (dark blue or gray)
- **Alignment**: All numbers center-aligned or right-aligned for easy comparison down columns

**Example 2B: Valuation Multiple Range Table**

A table comparing valuation ranges across different companies or time periods:

**Structural features**:
- **Company/metric columns**: First column lists metrics ("FQ1 to Date", "2019 YTD", "2018", "2017", "2016", "2015", "2014", "2014-2019")
- **Nested comparison columns**: Repeated structure for each entity being compared
  - "Raven": Low, High, Avg.
  - "Peer Index": Low, High, Avg.
- **Separator column**: Vertical line or light shading between comparison groups

**Content density**:
- **Multiple range**: Low, High, Average for each metric creating 3x density
- **Time series rows**: Historical data (2014-2019) plus current period (FQ1 to Date, 2019 YTD)
- **Mixed availability**: "n/a" for unavailable data points (Raven has n/a for 2017, 2016, 2015, 2014)
- **Multiples notation**: Values shown as "2.0x", "6.5x", "12.0x" with "x" suffix
- **Boxed highlights**: Average values might be boxed or shaded (e.g., "2.9x", "10.4x" in boxes)

**Visual sophistication**:
- **Column background alternation**: Each company group has subtle background shading for visual separation
- **Number formatting**: Consistent decimal places (one decimal: 2.0x, 6.5x)
- **Bold averages**: "Avg." column values in bold or with background highlighting
- **Border emphasis**: Heavier borders around the summary row ("2014-2019" showing full range)

**Example 2C: Ownership and Trading Metrics Comparison**

A table comparing multiple companies across diverse quantitative metrics:

**Structural features**:
- **Company columns**: 4-6 companies as columns (Goldfinch, Barbet, Blue Jay, Blackbird)
- **Metric rows**: 10-15 diverse metrics as rows:
  - "Blackbird / Blue Jay Ownership"
  - "S&P500 Constituent?"
  - "RMZ Constituent?"
  - "Research Coverage (# Analysts)"
  - "Structure" (REIT, Partnership, Corporation)
  - "Jurisdiction"
  - "Listing Exchange"
  - "3M ADTV (mm)"
  - "Liquidity (3M ADTV / Free Float)"
  - "2018E FFO Payout Ratio"
  - "# of Blackbird / Blue Jay Directors (/ Total Board Seats)"
  - "Management Structure"
  - "Voting Rights"

**Content density**:
- **Mixed data types within single table**:
  - Percentages: "33%", "56%", "70%", "0.88%", "0.04%", "64%", "80%", "23%"
  - Yes/No/NA: "Yes", "No", "NA", "Not Eligible", "Eligibility Unclear"
  - Counts: "18", "4", "8" (number of analysts)
  - Text labels: "REIT", "Partnership", "Corporation"
  - Geography: "Delaware", "Bermuda", "Ontario, Canada"
  - Exchanges: "NYSE", "NASDAQ", "NASDAQ / TSX", "NYSE / TSX"
  - Dollar amounts: "$108.3", "$2.2", "$49.7" (millions)
  - Fractions: "3 / 9", "2 / 9" (board seats)
  - Descriptions: "Internal", "External", "Proportional to ownership", "None, Blackbird has full control"
- **Color-coded answers**: Green text for "Yes", red for "No", black for neutral facts
- **Greater-than signs**: "> 56%" showing minimum values

**Visual sophistication**:
- **Row shading alternation**: Every other row has light gray background for readability
- **Header row**: Bold, colored background (dark blue) with white text
- **Alignment variety**: 
  - Text left-aligned ("Delaware", "REIT")
  - Numbers right-aligned ("$108.3", "18")
  - Yes/No center-aligned
- **Font variations**: 
  - Company names in headers might use specific brand colors
  - "NA" or "Not Eligible" in lighter gray text
- **Cell borders**: Grid lines visible but subtle; heavier borders every 3-4 rows to create visual groupings
- **Wrapped text**: Cells with longer descriptions (Voting Rights row) allow text wrapping to 2-3 lines

#### Category 3: Qualitative Comparison Tables

These tables contain substantial text, explaining deal structures, conditions, strategic rationales, or qualitative factors. They often look more like structured documents than traditional data tables.

**Example 3A: Deal Consideration Structure Table**

A detailed breakdown of merger consideration terms with extensive text:

**Structural features**:
- **Two-column layout**: 
  - Left column (20-30% width): Section headers/categories
  - Right column (70-80% width): Detailed explanations
- **Major sections** (each with colored background in left column):
  - "Acquirer"
  - "Consideration to Common Shareholders"
  - "Aggregate Consideration"
  - "Pro Forma Ownership of Blue Jay"
- **Merged cells**: Left column section headers span multiple right column entries when one section has sub-bullets

**Content density**:
- **Bullet hierarchies**: 2-3 levels of bullets within a single cell
  - Main bullet: "Payable in form of merger consideration:"
  - Sub-bullet: "— $9.25 billion less aggregate cash component of dividend less amounts payable to holders of operating partnership units and certain equity awards"
  - Sub-sub-bullet: "— Minimum merger aggregate consideration amount of $200mm equates to $0.31 cash per share"
- **Inline calculations**: Formulas embedded in text ("$23.50 per share less per share merger consideration amount")
- **Parenthetical clarifications**: "(Based on Min. Merger Consideration)", "(including payments to holders of operating partnership units and certain equity awards)"
- **Emphasis text**: Bold for key terms ("Blue Jay", "$9.25 billion", "61% / 39%")
- **Specific values**: "$9.25bn", "$200mm", "$0.31", "$23.50", "$23.19", "0.9866", "254mm", "26%", "52%"
- **Reference to other docs/prices**: "based on Blue Jay's closing price on 23-Mar-2018", "Represents 0.9866 Blue Jay units or Barbet shares"

**Visual sophistication**:
- **Section color coding**: Left column headers in distinct color blocks (dark blue, teal, or gray)
- **White text on dark backgrounds**: Section header text in white for contrast
- **Hierarchical indentation**: Each bullet level indented further (—, sub-dash levels)
- **Highlighting key metrics**: Yellow highlighting on critical values ("$23.19", "61% / 39%", "$21.79")
- **Font sizing**: Section headers slightly larger (14pt), body text standard (10-11pt), footnote markers smaller (8pt)
- **Line spacing**: Generous spacing within cells (1.2-1.3x) for readability despite text density
- **Bullet styles**: Em-dashes (—) instead of standard bullets for professional appearance
- **Red boxes**: Key final metrics outlined or boxed in red ("Final Offer, Current Pricing (23-Mar-2018)")

**Example 3B: Acquisition Terms and Conditions Table**

A comprehensive table laying out deal structure across multiple dimensions:

**Structural features**:
- **Fixed left column**: 25% width, containing category labels:
  - "ACQUIRERS"
  - "OFFER PRICE"
  - "PRO FORMA OWNERSHIP"
  - "REQUIRED APPROVALS"
  - "CONDITIONS"
  - "FINANCING"
  - "OTHER"
- **Wide right column**: 75% width, containing detailed multi-line explanations
- **Consistent row heights**: Each category gets sufficient height for 3-10 lines of text

**Content density**:
- **Listed entities**: "Kien Huat Realty III Limited ("Kien Huat")", "Genting Malaysia Berhad (through its subsidiary Genting (USA) Limited) ("Genting Malaysia")"
- **Detailed pricing**: "$9.74 per share in cash" followed by "— Represents a premium of 1.9% to the closing price as of August 2nd, 2019"
- **Ownership splits**: "51% by Kien Huat", "49% by Genting Malaysia"
- **Multi-part conditions**: 5 sub-items under CONDITIONS:
  - "(a) the completion of due diligence, including financial, legal, accounting, tax and business and operations diligence, with results satisfactory to us in our sole discretion;"
  - "(b) the negotiation and execution of mutually acceptable definitive transaction documentation;"
  - "(c) successfully obtaining all required or advisable regulatory approvals including, without limitation, any required approvals under the New York State Gaming Commission;"
  - "(d) satisfaction of any other applicable regulatory or stock exchange requirements; and"
  - "(e) such other terms and conditions as are usual and customary in comparable transactions"
- **Full sentences**: Each cell contains complete, grammatically correct sentences with proper punctuation
- **Lettered sub-lists**: (a), (b), (c), (d), (e) for sequential conditions
- **Specific regulatory bodies**: "New York State Gaming Commission", "Special Committee"
- **Legal precision**: "holders of a majority of the votes represented by the outstanding shares of Common Stock and Series B Preferred Stock not owned by the Acquirers, or their affiliates and associates and executive management"

**Visual sophistication**:
- **Dark left column**: Charcoal or dark blue background with white text for category labels
- **White right column**: White or very light gray background with black text
- **All caps headers**: Left column labels in all caps for emphasis ("ACQUIRERS", "CONDITIONS")
- **Vertical alignment**: Text in left column vertically centered; right column text top-aligned
- **Bold key terms**: "Kien Huat", "Genting Malaysia", "$9.74", specific percentages, regulatory body names
- **Consistent bullet style**: Em-dashes or custom bullets for sub-items
- **Tight line spacing**: 1.1-1.2x line height to fit extensive text while maintaining readability
- **Border structure**: Thick outer border, medium borders between rows, no vertical border between the two columns (or very subtle)

**Example 3C: Exchange Transaction Terms Table**

A table explaining complex security exchange terms with embedded calculations:

**Structural features**:
- **Two-column format**: 
  - Left column (~25%): "Terms" (section labels in gray background)
  - Right column (~75%): "Details" (detailed explanations in white background)
- **5-7 row sections**:
  - "Exchange Ratio"
  - "Form of Consideration"
  - "Implied Price"
  - "Implied Premium"
  - "Implied Ownership in Pro Forma Company"
  - "GLIB Series A Cumulative Redeemable Preferred Stock" (might expand to multiple rows)

**Content density**:
- **Complex exchange formulas**: "0.5800 Liberty Broadband (LBRD) Series C shares (non-voting) for each GCI Liberty (GLIB) Series A, 0.5800 Liberty Broadband (LBRD) Series B shares (10 votes) for each GCI Liberty (GLIB) Series B share"
- **Multiple implied prices**: 
  - "$72.49/share for GLIB Series A (1 vote) based on LBRD Series C (non-voting) closing price of $124.98 on 6/29/20"
  - "$71.92/share for GLIB Series B (10 votes) based on LBRD Series B (10 votes) closing price of $124.00 on 6/29/20"
- **Premium calculations**: "Implies a 8.1% premium to GLIB Series A shareholders, based on GLIB Series A unaffected closing price of $67.05 as of 6/29/20 for publicly traded shares"
- **Ownership percentages**: "Liberty management (John Malone and Greg Maffei) economic ownership is 8.2% and voting interest is 66.8%", "GLIB public economic ownership is 28.9% and voting interest is 3.9%", "LBRD public economic ownership is 64.8% and voting interest is 29.5%"
- **Stock terms**: "Rollover into a Liberty Broadband preferred instrument with same terms: No conversion rights; voting rights with voting common (1/3 of a vote / share); Dividends, payable quarterly, accrue on a daily basis at a rate of 7.00% per annum; If Liberty Broadband fails to pay dividend for any four quarterly periods, dividend rate increases by 2% annual rate until cured; Mandatory redemption date of March, 2039"
- **Parenthetical qualifiers**: Throughout, extensive use of parentheses to clarify terms

**Visual sophistication**:
- **Gray left column**: Light gray background for "Terms" column with black text
- **White right column**: White background for "Details" with black text
- **Bold securities**: "Liberty Broadband (LBRD)", "GCI Liberty (GLIB)" in bold
- **Bullet points**: Arrow symbols (►) or standard bullets for sub-items within cells
- **Inline emphasis**: Key figures like "$72.49/share", "8.1%", "0.5800" in bold
- **Multi-line cells**: Cells expand to 5-10 lines as needed; no text truncation
- **Footnote references**: Superscript numbers (¹) linking to explanatory notes below table
- **Red boxes or highlights**: Critical metrics like aggregate premium "$585mm or $5.44 per GLIB Series A share and $4.82 per GLIB Series B share¹" might be boxed or highlighted in yellow/red

**Key Principles Across All Table Categories:**

1. **Information Hierarchy**: Despite text/data density, clear hierarchy through indentation, bold, colors, borders
2. **Mixed Content Types**: Numbers, text, bullets, formulas, parenthetical notes all coexist in single tables
3. **Precise Alignment**: Left/right/center alignment used purposefully based on content type
4. **Border Strategy**: Varied border weights create visual sections without empty rows
5. **Color Coding**: Section headers, key metrics, and categories use consistent color schemes
6. **Footnote Integration**: Superscript markers link cells to detailed notes below table
7. **Text Wrapping**: Cells expand vertically as needed; horizontal space maximized
8. **No Truncation**: All content visible; no "..." or cut-off text even in dense tables
9. **Contextual Formatting**: Parentheses for negatives, "NM" for not meaningful, "n/a" for unavailable, italics for projections
10. **Professional Typography**: Em-dashes instead of hyphens, proper quotation marks, consistent spacing

When you search for table components, examine the returned images carefully. A simple grid with sparse text is likely not sophisticated enough. Look for tables that pack substantial information - where you can barely fit all the text in the cells, where there are clear hierarchies in the headers, where separator styling creates breathing room without empty rows, where multiple data types coexist, and where every cell serves a specific analytical purpose.
`.trim();







// =============================================================================
// STYLE CONSISTENCY
// =============================================================================

/**
 * Style consistency rules for typography and colors
 */
export const STYLE_CONSISTENCY = `
### Mixed Content Layouts

Many of the most effective banking slides combine multiple content types in sophisticated arrangements:

**Chart + Table combinations**: A primary visualization (bar chart, line chart, scatter plot) occupies 60-70% of the content area, with a supplementary data table in the remaining space showing exact values, summary statistics, or supporting metrics. The table and chart are visually coordinated - same color scheme, aligned edges, complementary rather than redundant.

**Grid arrangements**: Four to six related visualizations arranged in a regular grid (2x2, 2x3, 3x2). Each cell might be a chart, a table, a KPI callout box, or a mix. The grid has consistent spacing, shared legends or labels, and often uses separator lines or subtle backgrounds to delineate sections. Some grid cells might deliberately span multiple columns/rows for emphasis.

**Layered compositions**: A base element (large chart or table) with overlay elements (callout boxes with arrows, translucent highlight regions, embedded smaller charts or tables, annotation clusters). These layers create depth and allow dense information packing - you're using the Z-axis (visual layering) in addition to X and Y positioning.

**Process diagrams with data**: Org charts, transaction structures, or workflow diagrams where each node contains substantive text (multiple lines, bullet points) and possibly small data tables or metrics. Arrows between nodes are often annotated with transaction values, ownership percentages, or timing information.

When you examine reference slides, notice which of these patterns appear. The presentation style might heavily favor certain arrangements. Perhaps it consistently uses 2x2 grids for quarterly comparisons, or always places summary tables in the top-right corner, or frequently overlays translucent boxes with annotations. These stylistic patterns should guide your composition choices.

### Annotation and Callout Strategies

Dense slides often achieve their information richness through extensive annotation:

**Direct chart annotations**: Text labels pointing directly to specific data points, bars, or regions of a chart. These might explain outliers ("spike due to M&A activity"), provide context ("industry median"), or highlight insights ("30% CAGR"). Annotations are usually small text with subtle leader lines or arrows.

**Highlight regions**: Translucent colored boxes overlaid on charts to mark important periods or thresholds. A time series chart might have a light green box spanning Q2-Q3 labeled "High growth period" or a light red box for "Restructuring phase".

**Callout boxes**: Separate text boxes (often with subtle borders or fills) placed near relevant content, containing 2-5 lines of explanatory text, methodology notes, or interpretive insights. These aren't footnotes (which go at the bottom) but contextual information tied to specific visual elements.

**Reference lines**: Horizontal or vertical lines marking benchmarks, targets, or averages, with small labels explaining their significance ("Management guidance: $500M", "Pre-COVID baseline").

The key is that annotations add information, not just decoration. Each annotation should answer a question that the raw visualization leaves open. When you see heavily annotated components in your search results, study how they balance clarity with density - how small the text goes while remaining readable, how many annotations feel appropriate before it's cluttered, how leader lines are styled to be clear but unobtrusive.

### Typography and Text Hierarchy

Banking slides use typography to create information hierarchy without sacrificing density:

**Size variation**: Title at 14-22pt, section headers at 10-14pt, body text at 8-10pt, chart labels at 6-8pt, footnotes at 5-7pt. This wide range allows substantial text inclusion while maintaining clear hierarchy.

**Weight and style**: Bold for emphasis and headers, regular for body text, sometimes italic for notes or caveats. Underlining is rare but might appear in table headers.

**Font consistency**: Usually 1-2 font families maximum. Corporate presentations often stick to the company's brand font throughout. The presentation's style patterns will show you the font(s) used.

**Text wrapping and spacing**: Banking slides often use tight line spacing (1.0 - 1.25) in tables and dense text blocks to fit more content. Paragraphs might have minimal spacing between them. This is acceptable because the information hierarchy (via size and weight) maintains readability.

**Strategic bolding**: In dense text areas (table cells, bullet points, footnotes), key terms or values might be bolded to allow scanning. This isn't decoration - it's guiding the reader's eye to critical information.

When you search for text-heavy components, notice these patterns. The footnotes might be 7pt but perfectly readable because they're well-kerned and the font is clean. The table headers might use bold and a slightly larger size to stand out despite minimal white space above them.

### Color and Styling Principles

Banking slides tend toward conservative, purposeful color use:

**Limited palettes**: Often 3-5 colors total - primary brand color(s), accent colors for emphasis, neutral grays for backgrounds/separators. Color isn't decorative; each color means something (different business segments, performance categories, temporal periods).

**Functional color coding**: In charts, colors distinguish categories consistently throughout the presentation. Blue might always represent North America, green for Europe, orange for Asia-Pacific. Or colors might indicate performance - green for above target, red for below, gray for neutral.

**Subtle backgrounds**: Table rows might alternate between white and light gray (5-10% gray) for readability. Section backgrounds might use 3-5% tints of the brand color to delineate areas without overwhelming.

**Emphasis through contrast**: Key data points, outliers, or important sections might use a brighter or more saturated version of the color palette to draw attention.

The style patterns extracted from the presentation will show common colors. Use these consistently. If you see a color appearing frequently (say, a specific shade of blue at 30% usage), that's likely a primary brand color to use in your charts and highlights.

### Critical: Following the Presentation's Established Style Guide

The style patterns provided are not suggestions - they are the **required style language** of this specific presentation. You must match these patterns precisely:

**Chart Colors (CRITICAL)**:
- Study the color schemes used in chart components: which colors for which data series?
- If bar charts consistently use RGB(68, 114, 196) for primary data, use that exact color
- If trend lines are always RGB(237, 125, 49), match it precisely
- Note the order: if 5-series charts go blue, orange, gray, yellow, green - maintain that sequence
- Check if negative values use different colors (red vs black)
- Verify if certain business units or segments have consistent color assignments
- VALIDATION: Before finalizing, confirm each chart series uses colors that appear in the style patterns

**Fill Colors (CRITICAL)**:
- Table cell backgrounds: what shade of gray for alternating rows? (e.g., RGB(242, 242, 242))
- Header row fills: solid color or gradient? What exact RGB values?
- Shape fills for callout boxes, strapboxes, annotation boxes: which colors at what opacity?
- Highlight regions: what color at what transparency? (e.g., RGB(255, 255, 0) at 30% opacity)
- Section backgrounds: do some sections have subtle tinted backgrounds? What color/opacity?
- VALIDATION: Every filled shape should use a color that exists in the style patterns

**Font Colors (CRITICAL)**:
- Title text: usually black (RGB(0, 0, 0)) or dark gray (RGB(64, 64, 64))
- Body text: might be slightly lighter gray than titles
- Annotations/labels: often medium gray (RGB(127, 127, 127)) to differentiate from primary content
- Emphasis text: might use brand color instead of black
- Link text: usually brand blue or standard blue (RGB(0, 0, 255))
- Footnotes: often light gray (RGB(166, 166, 166))
- VALIDATION: Check that text color choices match the presentation's hierarchy patterns

**Font Families and Sizes (CRITICAL)**:
- Primary font family: (e.g., "Calibri", "Arial", "Helvetica Neue") - use this for 90%+ of text
- Secondary font family: (e.g., "Georgia", "Times New Roman") - might be used for emphasis or headers
- Title sizes: typically 18-28pt depending on slide type
- Subtitle sizes: typically 14-18pt
- Header/section label sizes: typically 12-16pt
- Body text: typically 10-12pt
- Chart axis labels: typically 8-10pt
- Chart data labels: typically 8-10pt
- Annotations: typically 8-10pt
- Footnotes: typically 7-9pt
- Table headers: typically 10-12pt (often bold)
- Table cell text: typically 9-11pt
- VALIDATION: Every text element should match size patterns for that text type

**Bullet Styling and Text Formatting (CRITICAL)**:
Banking presentations have very specific bullet and paragraph formatting that creates the professional, dense look:

**Bullet characters by level**:
- Level 1: Often solid circle (●) or en-dash (–) or custom symbol
- Level 2: Often hollow circle (○) or en-dash (–) or small solid square (▪)
- Level 3: Often small en-dash (–) or hollow square (□)
- Check components to see the exact progression used

**Indentation patterns**:
- Level 1 left indent: typically 0.125" or 0.25" (9-18pt)
- Level 2 left indent: typically 0.25" or 0.375" (18-27pt)
- Level 3 left indent: typically 0.375" or 0.5" (27-36pt)
- Hanging indent: typically 0.125" (9pt) - the space between bullet and text
- VALIDATION: Measure indents in component textboxes and replicate exactly

**Line spacing by context**:
- Slide titles: typically 1.0-1.15 line spacing, 0pt before, 6-12pt after
- Bullet lists: typically 1.0-1.15 line spacing, 0-6pt before each paragraph, 0-3pt after
- Dense table text: typically 0.9-1.0 line spacing for compactness
- Annotations: typically 1.0 line spacing, minimal paragraph spacing
- Process diagram text: typically 0.9-1.0 line spacing to fit text in nodes
- Footnotes: typically 1.0 line spacing, 0pt before/after (very compact)
- Strapbox text: typically 1.0 line spacing, vertically centered in box
- Labels on charts: typically single-line, no paragraph spacing needed
- VALIDATION: Check 3-4 components with each text type and measure their exact spacing values

**Paragraph spacing by context**:
- Between major sections: typically 12-18pt
- Between bullet points: typically 0-6pt
- Between text paragraphs: typically 6-12pt
- After headers before content: typically 6-12pt
- Before footnotes: typically 12-18pt to separate from main content
- VALIDATION: Measure spacing in similar content contexts from components

**Font styling patterns**:
- Bold: used for headers, emphasis words in bullets, table headers, key metrics
- Italic: used for notes, caveats, source attributions, sometimes for company names
- Underline: rarely used, might appear in headers or to emphasize specific terms
- ALL CAPS: sometimes used for section headers or strapbox labels
- Color emphasis: key numbers or terms might use brand color instead of bold
- VALIDATION: Check if bolding follows consistent rules (e.g., first word of bullets, all numbers, etc.)

### Style Extraction and Application Strategy

When you receive style patterns, they'll include:
1. **Top 10 style signatures**: Most common styling combinations with usage percentages
2. **Font frequencies**: Which fonts appear most often
3. **Color frequencies**: Which RGB values appear most often
4. **Size distributions**: Which font sizes are typical for which contexts

**How to apply style patterns**:
1. **Identify the dominant style**: The top 1-3 style signatures (highest usage %) are your primary templates
2. **Match by context**: If creating a bullet list, find bullet styling in the patterns; if creating a chart, find chart styling
3. **Be precise**: Don't approximate colors as "blue-ish" - use exact RGB values from patterns
4. **Maintain consistency**: If you use RGB(68, 114, 196) for one chart series, use it for all similar series
5. **When in doubt, copy**: If a component has the styling you need, copy its exact attributes

**VALIDATION CHECKLIST** (run this mentally before finalizing):
- [ ] Every color I used appears in the style patterns or components
- [ ] Every font size I used matches typical sizes for that text type
- [ ] Font family is consistent with presentation (90%+ same font)
- [ ] Bullet characters and indents match component examples
- [ ] Line spacing matches density patterns seen in components
- [ ] No arbitrary styling decisions - everything has a precedent in the presentation

### Footnotes and Citations

Banking slides are meticulous about sourcing and methodology:

**Footnote density**: It's not unusual to have 3-7 footnotes on a single slide, each 1-3 lines of small text explaining data sources, calculation methodologies, assumptions, or caveats.

**Footnote positioning**: Usually at the very bottom of the content area, in the smallest readable font size (7-9pt). Sometimes in a subtle gray color to visually separate from main content.

**Inline references**: Superscript numbers or symbols in the main content (chart labels, table cells, text blocks) linking to the footnotes. These are tiny but must be clear.

**Citation format**: For research-based content, citations should include enough detail to verify the source - not just "AlphaSense" but "Company name, Document type, Date" or similar context.

When you construct slides with research data, the execution instructions should specify where citations belong. Chart titles might have a footnote marker. Table headers might reference the data source. The more authoritative and verifiable the slide feels, the better.

### Component Selection and Adaptation

When you search for components, you're not looking for exact matches to copy. You're looking for sophisticated patterns to adapt:

**Search broadly first**: Initial searches should use general descriptive terms to see the range of sophistication available. "Quarterly performance chart" will show you whether the presentation style leans toward simple 4-bar charts or complex multi-layered compositions.

**Refine based on quality**: If initial results show simple patterns, refine searches with more specific sophistication indicators - "quarterly comparison grid with annotations" or "table showing nested categories with metrics". This helps surface the more complex examples.

**Compose from multiple components**: The best slide might combine elements from 2-4 different components. The header style from one, the chart arrangement from another, the table structure from a third, the footnote formatting from a fourth. You're creating a synthesis.

**Scale and position thoughtfully**: When adapting a component, maintain its proportions and internal relationships. If a component is a 2x2 chart grid where each chart occupies 250x180pt, and you're scaling it to fit a different content area, scale the entire group uniformly. Don't arbitrarily change the grid to 3x3 or make one chart much larger - that breaks the design rationale.

**Respect the component's complexity**: If a component shows a sophisticated table with nested headers and dense cells, your adapted version should maintain that sophistication level. Don't simplify it into a basic grid because that's easier. The component exists because this presentation style values that level of detail.

### Understanding the Style Through Search

The reference slides and components you retrieve are your window into what "good" means for this specific presentation. As you search, you're developing pattern recognition:

**Frequency indicates preference**: If you search for "comparison table" and 4 out of 5 results show tables with nested row headers and separator styling, that's the expected pattern. Your table should probably use that structure.

**Complexity distribution**: If chart components consistently show 8-12 data points rather than 3-4, that's a signal about appropriate density. If tables consistently have 6-8 columns rather than 3-4, match that complexity level.

**Layout conventions**: If similar slides (same master template) consistently place the main chart on the left 60% and supporting data on the right 40%, follow that convention. These patterns exist because they work for this audience and style.

**Styling consistency**: If the same shape styles (colors, fonts, line weights) appear across multiple components, that's the visual language of this presentation. Use those styles even when creating new elements not directly copied from components.

The more components you examine (within reason - don't search endlessly), the better you understand the presentation's quality bar. Four to six well-chosen component searches usually reveal the key patterns.

### The Goal: Information-Rich, Not Cluttered

The distinction between density and clutter is subtle but critical:

**Dense**: Every element serves a purpose, hierarchy is clear, related information is grouped, the eye can follow a logical reading path, and the content feels substantial but not overwhelming.

**Cluttered**: Elements overlap unintentionally, hierarchy is unclear, unrelated information is mixed, the eye doesn't know where to look first, and the content feels chaotic.

Achieving density requires:
- Ruthless purpose (does this element add information or just fill space?)
- Clear hierarchy (can a viewer immediately identify what's most important?)
- Logical grouping (are related items visually associated?)
- Balanced composition (is the visual weight distributed appropriately?)

When you construct a slide, continuously ask: "Am I adding information or just adding elements?" If a potential addition doesn't answer a question or provide context, reconsider it. But if it does add information, find a way to include it without sacrificing clarity.

### Trust Your Search Results

The components and reference slides returned by your search are prequalified - they exist in a presentation that the user provided as the style guide. If a component looks sophisticated and complex, that's likely the quality bar you should target. If reference slides show dense, multi-layered compositions, that's what "good" looks like for this context.

Don't second-guess toward simplicity. If you search for a chart and get back a complex multi-panel visualization with extensive annotations, the correct response is "I should create something at this sophistication level," not "this seems too complex, I'll simplify it."

Similarly, if all the tables you find have 6+ columns and nested headers, that's the pattern to follow. The user chose this presentation as the reference because they want slides at this level of sophistication.

Your role is to understand, adapt, and synthesize these patterns into a new slide that serves the requested content while maintaining the quality bar demonstrated by the source presentation.

---
`.trim();

// =============================================================================
// RULES FOR SLIDE CONSTRUCTION
// =============================================================================

/**
 * Rules for slide construction
 */
export const RULES_FOR_SLIDE_CONSTRUCTION = `
## Critical Rules for Slide Construction

### Master Template Boundaries

**ABSOLUTE REQUIREMENTS**:
1. ALL content must be positioned INSIDE the content area boundaries
2. NO content can be placed in exclusion zones (these contain master elements like headers, footers, logos)
3. Violating these boundaries will cause overlaps with master template elements and ruin the slide

The master template defines:
- **Slide dimensions**: Total canvas size (provided in slideDimensions parameter)
- **Content area**: The SAFE ZONE where you place all slide content
- **Exclusion zones**: FORBIDDEN AREAS that must remain empty

When positioning elements:
- Check element bounds against content area boundaries
- Ensure no shapes extend beyond the content area
- Leave small margins (5-10 points) from content area edges for safety
- Group related elements and position the group as a whole

### Shape Copying and Scaling

When copying components:
1. **Maintain relative positions**: If a component has 3 shapes in specific positions, keep their spatial relationships when scaling
2. **Scale proportionally**: Use uniform scaling factors for width and height
3. **Group integrity**: If shapes are grouped in the component, maintain that grouping
4. **Style preservation**: Copy all styling attributes (colors, fonts, line weights, fills)

### Research Data Integration

When using AlphaSense research data:
1. **Extract relevant information**: Parse research results for specific data points, metrics, quotes
2. **Populate content systematically**: Match research data to specific chart series, table cells, or text blocks
3. **Add citations**: Every shape using research data should have metadata: {'commentary': str, 'link': str}
4. **Maintain accuracy**: Don't modify or interpret data - use exact values and quotes

### Style Consistency

Apply style patterns from the presentation:
1. **Fonts**: Use the common fonts identified in style patterns (typically 1-2 font families)
2. **Colors**: Use the primary brand colors consistently across all elements
3. **Sizing**: Follow the typical size ranges for different text types (headers, body, labels, footnotes)
4. **Spacing**: Match the typical spacing patterns (line spacing, paragraph spacing, margins)

### Information Density Guidelines

Target these metrics for a well-constructed banking slide:
- **Total word count**: 200-300 words across all text elements (headers, labels, annotations, footnotes)
- **Chart data points**: 8-15+ data points with individual labels where appropriate
- **Footnotes**: 3-7 footnotes of 1-3 lines each for sourcing and methodology
- **Visual elements**: Multiple charts, tables, or mixed content - rarely just a single element
- **Annotations**: 2-5 callouts, highlights, or annotations adding context to visualizations

These are guidelines, not rigid rules. The specific content and instructions may call for more or less density.
`.trim();

// =============================================================================
// ARTIFACT ID USAGE
// =============================================================================

/**
 * How to use artifact IDs for charts, tables, and images
 */
export const ARTIFACT_ID_USAGE = `
### Artifact ID Usage

Context items are passed with specific IDs that you must use correctly to link dynamic content.

**Excel Charts and Tables (artifact_xxx):**
- Use \`artifact_xxx\` IDs when inserting charts or tables from Excel
- These link to specific workbook ranges or chart objects
- Set the artifact metadata so the system can track the source

**Image Fills:**
- \`logo_xxx\` - Company or brand logos
- \`wikimedia_xxx\` - Images from Wikimedia Commons
- \`asset_xxx\` - Images from the presentation's asset library
- Use these IDs in shape fill properties, not as separate image objects

**Setting Metadata:**
- When inserting an artifact, always set the corresponding metadata
- This enables features like refresh, edit source, and tracking
- Include the original context item reference in the metadata
`.trim();

// =============================================================================
// AREA DEFINITION (for MultiSlideBuilder)
// =============================================================================

/**
 * Best practices for defining areas in MultiSlideBuilder
 */
export const AREA_DEFINITION_BEST_PRACTICES = `
### Area Definition Best Practices

When defining areas for the ExecutionAgent, think like an architect dividing a floor plan.

**Interpreting Reference Slides:**
- Study the reference slide's visual structure
- Identify logical content regions (header, main, sidebar, footer)
- Note how the reference balances density vs whitespace
- Map your content needs to similar regions

**When to Use Dense vs Sparse:**
- Dense: Data-heavy slides (financial summaries, comparisons, dashboards)
- Sparse: Title slides, section dividers, single-focus slides
- Mixed: Most content slides with headline + supporting data

**Determining Content Type:**
- "text": Primarily narrative, bullets, or labels
- "chart": Data visualizations, graphs, diagrams
- "table": Structured data in rows/columns
- "mixed": Combination requiring flexible layout

**Region Guidelines:**
- Normalize coordinates to 0-1 range (relative to content area)
- Avoid overlapping regions
- Leave small gaps between adjacent regions
- Ensure regions cover the full content area
`.trim();

// =============================================================================
// STYLE GUIDE USAGE (for MultiSlideBuilder)
// =============================================================================

/**
 * How to interpret and use the style guide
 */
export const STYLE_GUIDE_PHILOSOPHY = `
### Understanding the Style Guide

The style guide reduces "temperature" - it provides structure and consistency without being overly prescriptive.

**What the Style Guide Provides:**
- Available layouts and their intended purposes
- Color palette with semantic meanings
- Typography hierarchy and font specifications
- Example slides showing acceptable patterns
- Spacing and alignment conventions

**How to Use It:**
- Search for slides similar to what you're building
- Use matching layouts when available
- Follow the style patterns (colors, fonts, spacing)
- Don't copy content—adapt the visual structure

**When to Deviate:**
- Style guide doesn't have a matching pattern
- Content requirements demand a different approach
- Client specifically requests something different
- But always maintain color and typography consistency
`.trim();

/**
 * How to use the presentationLibrarySearchAgent effectively
 */
export const SEARCH_AGENT_GUIDANCE = `
### Using Reference Search Effectively

The presentationLibrarySearchAgent finds relevant slides from the style guide. Use it strategically.

**Good Search Queries:**
- "comparison table with financial metrics"
- "executive summary with key highlights"
- "timeline showing project phases"
- Be specific about content type and purpose

**Using Search Results:**
- Study the layout structure, not just the content
- Note spacing patterns and visual hierarchy
- Identify reusable chart/table formats
- Extract color usage patterns

**Multiple Searches:**
- Search for different aspects of a complex slide
- One search for the layout pattern
- Another for specific chart types
- Combine insights from multiple results
`.trim();

// =============================================================================
// COMPOSITE SECTIONS
// =============================================================================

/**
 * Combined density section (overview + guidelines + whitespace)
 */
export const DENSITY_SECTION_FULL = `
${IB_QUALITY_OVERVIEW}

${DENSITY_GUIDELINES}

${WHITESPACE_PHILOSOPHY}
`.trim();

/**
 * Combined visualization section (charts + tables)
 */
export const VISUALIZATION_SECTION_FULL = `
${CHART_SOPHISTICATION}

${TABLE_SOPHISTICATION}
`.trim();

/**
 * Combined style section (consistency + boundaries + artifacts)
 */
export const STYLE_SECTION_FULL = `
${STYLE_CONSISTENCY}

${RULES_FOR_SLIDE_CONSTRUCTION}

${ARTIFACT_ID_USAGE}
`.trim();

/**
 * Full prompt content for ExecutionAgent
 */
export const EXECUTION_AGENT_PROMPT_CONTENT = `
${DENSITY_SECTION_FULL}

${VISUALIZATION_SECTION_FULL}

${STYLE_SECTION_FULL}
`.trim();

/**
 * Full prompt content for MultiSlideBuilder
 */
export const MULTI_SLIDE_BUILDER_PROMPT_CONTENT = `
${DENSITY_SECTION_FULL}

${STYLE_GUIDE_PHILOSOPHY}

${SEARCH_AGENT_GUIDANCE}

${AREA_DEFINITION_BEST_PRACTICES}
`.trim();

// =============================================================================
// SLIDE STATE FEEDBACK TEMPLATE
// =============================================================================

/**
 * Parameters for generating slide state feedback
 */
export interface SlideStateFeedbackParams {
    slideTitle: string;
    slideIndex: number;
    areasBuiltForSlide: number;
    totalAreas: number;
    success: boolean;
    errors: string[];
    shapeCount: number;
    checkpointId: string;
    slideJson: string;
}

/**
 * Generate the slide state feedback content (without reminder tags).
 * 
 * This content is shown to the agent after each buildArea call to help it
 * evaluate progress and decide what to do next.
 * 
 * @param params - Feedback parameters
 * @returns Formatted feedback text (caller should wrap in reminder tags)
 */
export function generateSlideStateFeedbackContent(params: SlideStateFeedbackParams): string {
    const {
        slideTitle,
        slideIndex,
        areasBuiltForSlide,
        totalAreas,
        success,
        errors,
        shapeCount,
        checkpointId,
        slideJson,
    } = params;
    
    return `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🎯 CURRENT SLIDE STATE: "${slideTitle}" (index ${slideIndex})
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

**Build Progress:**
- Areas built on this slide: ${areasBuiltForSlide}/${totalAreas}
- Last build: ${success ? '✓ Success' : '✗ Failed'}${errors.length > 0 ? ` (${errors.join(', ')})` : ''}
- Shapes created in last build: ${shapeCount}
- Checkpoint: ${checkpointId}

**Slide Breakdown (JSON):**
\`\`\`json
${slideJson}
\`\`\`

**Instructions:**
Here's the breakdown of the last slide you edited along with an image of it.
Compare your progress on it to your requirements and guidelines.
Decide what to do next:
- Build more areas on this slide (if areas remain)
- Move to the next slide (if this slide is complete)
- Call submitResults (if all slides are complete)

⚠️ DO NOT try to fix anything you perceive to be a mistake caused by a previous step.
You CANNOT delete or edit existing shapes - only add new ones.
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`;
}

// =============================================================================
// SHARED CONSTANTS
// =============================================================================

/**
 * Prefixes for context IDs that represent images (stored in image cache)
 * Used to determine which context items should be included as images vs text
 */
export const IMAGE_CONTEXT_PREFIXES = ['logo', 'wikimedia', 'artifact', 'asset'] as const;

// =============================================================================
// SHARED UTILITIES
// =============================================================================

/**
 * Ensure a base64 string is formatted as a data URL
 * 
 * @param base64 - Raw base64 string or data URL
 * @param mimeType - MIME type to use if not already a data URL (default: 'image/png')
 * @returns Properly formatted data URL
 */
export function ensureDataUrl(base64: string, mimeType: string = 'image/png'): string {
    if (base64.startsWith('data:')) {
        return base64;
    }
    return `data:${mimeType};base64,${base64}`;
}

/**
 * Extract raw base64 from a data URL, handling edge cases
 * 
 * @param dataUrl - Data URL or raw base64
 * @returns Raw base64 string
 */
export function extractBase64FromDataUrl(dataUrl: string): string {
    if (!dataUrl.startsWith('data:')) {
        return dataUrl;
    }
    const commaIndex = dataUrl.indexOf(',');
    if (commaIndex === -1) {
        // Malformed data URL without comma - return as-is
        return dataUrl;
    }
    return dataUrl.substring(commaIndex + 1);
}

// =============================================================================
// AVAILABLE SLIDES PROMPT SECTION
// =============================================================================

/**
 * Parameters for generating the "Available Slides" prompt section
 */
export interface AvailableSlidesParams {
    styleGuideSlideCount: number;
    workingSlideCount: number;
}

/**
 * Generate the "Available Slides" prompt section
 * 
 * This section explains to the agent what slides are available and how to read them.
 * Used by both MultiSlideBuilderAgent and ExecutionAgent.
 * 
 * @param params - Slide count parameters
 * @returns Formatted prompt section
 */
export function generateAvailableSlidesSection(params: AvailableSlidesParams): string {
    const { styleGuideSlideCount, workingSlideCount } = params;
    
    return `## Available Slides

**Style guide slides:** ${styleGuideSlideCount} hidden reference slides (indices 1-${styleGuideSlideCount})
- Use these as visual references for styling, layout patterns, and density
- Read with: readSlide({ styleGuideSlides: { indexes: [1, 2], detailLevel: "complete" } })

**Working slides:** ${workingSlideCount} slides to build (numbered 1-${workingSlideCount})
- These are numbered starting from 1 (the tool handles index offset automatically)
- Read with: readSlide({ workingSlides: { indexes: [1], detailLevel: "complete" } })

The tool auto-resolves indices - just use 1, 2, 3... for whichever slide type you need.`;
}
